/**
 * Legal Template Registry
 *
 * Merges the 4 template sources into a single picker list at render time:
 *   1. static LEGAL_TEMPLATES (the 8 legacy + 1 general)
 *   2. users/{uid}/legalTemplates (user-saved)
 *   3. tenants/{tenantId}/legalTemplates (firm-saved)
 *   4. globalLegalTemplates where status === 'active' (platform-published)
 *
 * Deduplication priority (highest wins): static > user > tenant > global.
 * This ensures a user-saved override of a legacy template would not silently
 * replace the vetted static version.
 *
 * Also provides the save-as-template flow and auto-approve heuristic used
 * when a user promotes a freeform draft into a reusable template.
 */

import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

import { LEGAL_TEMPLATES, type LegalTemplate, type LegalTemplateField } from './index';
import { resolveTenantId } from '../../services/tenantResolver';

// ============================================================================
// Types for saved / dynamically-loaded templates
// ============================================================================

export type TemplateScope = 'static' | 'user' | 'tenant' | 'global';

export interface SaveTemplateInput {
  /** Human-readable title (defaults to drafter-inferred conventional doc name) */
  title: string;
  /** Short description of what the template produces */
  description: string;
  /** Scope selected by the user */
  scope: 'user' | 'tenant' | 'global-candidate';
  /** Field schema auto-suggested from what the user filled in */
  fields: LegalTemplateField[];
  /** Attorney-review flag (default false for user-saved per Miles #4) */
  requiresAttorneyReview: boolean;
  /**
   * The generated document body (used by the auto-approve heuristic and
   * stored alongside the template for review context).
   */
  generatedBody: string;
  /** Language the user authored in at save time */
  authoredLanguage: 'en' | 'es';
  /** English freeform drafter system prompt */
  freeformDrafterPromptEn: string;
  /** Spanish freeform drafter system prompt (auto-generated if needed) */
  freeformDrafterPromptEs: string;
}

export interface SaveTemplateResult {
  templateId: string;
  scope: TemplateScope;
  status: 'active' | 'pending-review';
  autoApproved: boolean;
  reason?: string;
}

// ============================================================================
// Auto-Approve Heuristic (Layer D.4)
//
// AUTO-APPROVE if the saved template body does not contain:
//   - firm-specific branding strings (law firm names, attorney names)
//   - specific client names
//   - hard-coded dollar amounts
//   - specific matter/case numbers
//   - AND the inferred category is a recognized generic legal practice area
// ============================================================================

const GENERIC_LEGAL_CATEGORIES = [
  'motion',
  'notice',
  'agreement',
  'letter',
  'memo',
  'memorandum',
  'subpoena',
  'declaration',
  'demand',
  'contract',
  'petition',
  'brief',
  'stipulation',
  'affidavit',
];

/**
 * Heuristic check: does the template body look generic enough to auto-publish?
 *
 * Returns { approved: boolean, reason?: string }. When approved=false, reason
 * explains what blocked auto-approval so the review queue entry can show it
 * to the reviewer.
 */
export function isAutoApprovable(
  templateBody: string,
  templateMeta: { title: string; description: string }
): { approved: boolean; reason?: string } {
  const body = (templateBody || '').toLowerCase();
  const meta = `${templateMeta.title} ${templateMeta.description}`.toLowerCase();

  // 1. Hard-coded dollar amounts (e.g. "$5,000", "$1,250,000.00")
  if (/\$\s*[0-9][0-9,]*(?:\.[0-9]+)?/.test(templateBody)) {
    return { approved: false, reason: 'Contains hard-coded dollar amount(s).' };
  }

  // 2. Specific matter/case numbers (common formats: ADJ1234567, 22-CV-01234, etc.)
  if (/\b(?:case|matter|claim|adj|docket|no\.?|number)\s*[:#]?\s*[A-Z0-9][-A-Z0-9]{3,}/i.test(templateBody)) {
    return { approved: false, reason: 'Contains what appears to be a specific case/matter number.' };
  }
  if (/\b(ADJ\d{5,}|[0-9]{2}-(?:CV|CR|FL|PR|CU|CE)-\d{4,})\b/i.test(templateBody)) {
    return { approved: false, reason: 'Contains a case/matter number.' };
  }

  // 3. Firm-specific branding / law firm names
  //    Flag strings like "Law Offices of", "& Associates", "LLP", "The Law Firm of",
  //    and brittle cues like obvious attorney name blocks.
  const firmCues = [
    /\blaw\s+offices?\s+of\b/,
    /\blaw\s+firm\s+of\b/,
    /\b&\s+associates\b/,
    /\bllp\b/,
    /\bprof(?:\.|essional)\s+corp(?:oration)?\b/,
  ];
  for (const rx of firmCues) {
    if (rx.test(body)) {
      return { approved: false, reason: 'Contains firm-specific branding (e.g. "Law Offices of", "LLP").' };
    }
  }

  // 4. Attorney-of-record blocks ("Attorney for Plaintiff", "State Bar No. 123456")
  if (/\bstate\s+bar\s+no\.?\s*\d+/i.test(templateBody)) {
    return { approved: false, reason: 'Contains a specific State Bar number.' };
  }
  if (/\battorney\s+for\s+(?:plaintiff|defendant|respondent|petitioner|applicant|client)/i.test(templateBody)) {
    // Attorney-of-record block is OK in a template as long as the names aren't
    // filled in — we only flag if a proper-noun name appears right after.
    const m = templateBody.match(/\battorney\s+for\s+(?:plaintiff|defendant|respondent|petitioner|applicant|client)\b[\s\S]{0,80}/i);
    if (m && /[A-Z][a-z]+\s+[A-Z][a-z]+/.test(m[0]) && !/\[.*\]/.test(m[0])) {
      return { approved: false, reason: 'Contains a specific attorney-of-record block with named attorney.' };
    }
  }

  // 5. Recognized generic legal practice area — inferred from title/description
  const hasGenericCategory = GENERIC_LEGAL_CATEGORIES.some((c) => meta.includes(c));
  if (!hasGenericCategory) {
    return { approved: false, reason: 'Title/description does not map to a recognized generic legal category (motion, notice, agreement, letter, memo, subpoena, declaration, etc.).' };
  }

  return { approved: true };
}

// ============================================================================
// Slugify helper for Firestore doc IDs
// ============================================================================

function slugifyTitle(title: string): string {
  return (title || 'template')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'template';
}

// ============================================================================
// Firestore shape for a saved template document
// ============================================================================

interface StoredTemplateDoc {
  id: string;
  name: string;
  nameEs: string;
  description: string;
  descriptionEs: string;
  category: LegalTemplate['category'];
  jurisdiction: LegalTemplate['jurisdiction'];
  fields: LegalTemplateField[];
  sections: LegalTemplate['sections'];
  requiresAttorneyReview: boolean;
  isFreeform: boolean;
  scope: TemplateScope;
  freeformDrafterPromptEn?: string;
  freeformDrafterPromptEs?: string;
  generatedBody?: string;
  authoredLanguage?: 'en' | 'es';
  submittedBy?: string;
  submittedAtMs?: number;
  tenantId?: string;
  status?: 'active' | 'pending-review';
  createdAt?: Timestamp;
}

function storedToTemplate(stored: StoredTemplateDoc, scope: TemplateScope): LegalTemplate {
  return {
    id: stored.id,
    name: stored.name,
    nameEs: stored.nameEs,
    description: stored.description,
    descriptionEs: stored.descriptionEs,
    category: stored.category,
    jurisdiction: stored.jurisdiction,
    fields: stored.fields || [],
    sections: stored.sections || [
      { id: 'freeform', title: 'Document', titleEs: 'Documento', content: '', contentEs: '' },
    ],
    requiresAttorneyReview: stored.requiresAttorneyReview ?? false,
    isFreeform: stored.isFreeform ?? true,
    scope,
    freeformDrafterPromptEn: stored.freeformDrafterPromptEn,
    freeformDrafterPromptEs: stored.freeformDrafterPromptEs,
  };
}

// ============================================================================
// Loaders for dynamic scopes (safe to fail; empty array on error)
// ============================================================================

async function loadUserTemplates(): Promise<LegalTemplate[]> {
  try {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    const db = getFirestore(getApp());
    const snap = await getDocs(collection(db, `users/${uid}/legalTemplates`));
    const out: LegalTemplate[] = [];
    snap.forEach((d) => {
      const data = d.data() as StoredTemplateDoc;
      out.push(storedToTemplate({ ...data, id: data.id || d.id }, 'user'));
    });
    return out;
  } catch (err) {
    console.warn('[templateRegistry] loadUserTemplates failed', err);
    return [];
  }
}

async function loadTenantTemplates(): Promise<LegalTemplate[]> {
  try {
    const tenantId = await resolveTenantId();
    if (!tenantId) return [];
    const db = getFirestore(getApp());
    const snap = await getDocs(collection(db, `tenants/${tenantId}/legalTemplates`));
    const out: LegalTemplate[] = [];
    snap.forEach((d) => {
      const data = d.data() as StoredTemplateDoc;
      out.push(storedToTemplate({ ...data, id: data.id || d.id }, 'tenant'));
    });
    return out;
  } catch (err) {
    console.warn('[templateRegistry] loadTenantTemplates failed', err);
    return [];
  }
}

async function loadGlobalActiveTemplates(): Promise<LegalTemplate[]> {
  try {
    const db = getFirestore(getApp());
    const q = query(
      collection(db, 'globalLegalTemplates'),
      where('status', '==', 'active')
    );
    const snap = await getDocs(q);
    const out: LegalTemplate[] = [];
    snap.forEach((d) => {
      const data = d.data() as StoredTemplateDoc;
      out.push(storedToTemplate({ ...data, id: data.id || d.id }, 'global'));
    });
    return out;
  } catch (err) {
    console.warn('[templateRegistry] loadGlobalActiveTemplates failed', err);
    return [];
  }
}

// ============================================================================
// Public: merged template list for the picker
//
// Dedup priority: static > user > tenant > global. Higher-priority sources
// win on templateId collision, so a user cannot shadow a legacy static
// template by saving one with the same id.
// ============================================================================

export async function loadAllAvailableTemplates(): Promise<LegalTemplate[]> {
  const [userTpls, tenantTpls, globalTpls] = await Promise.all([
    loadUserTemplates(),
    loadTenantTemplates(),
    loadGlobalActiveTemplates(),
  ]);

  const merged = new Map<string, LegalTemplate>();

  // Lowest priority first so higher-priority sources overwrite
  for (const t of globalTpls) merged.set(t.id, t);
  for (const t of tenantTpls) merged.set(t.id, t);
  for (const t of userTpls) merged.set(t.id, t);
  // Static always wins
  for (const t of LEGAL_TEMPLATES) merged.set(t.id, { ...t, scope: t.scope || 'static' });

  return Array.from(merged.values());
}

// ============================================================================
// Save-as-template flow (Layer D.1 + D.2 + D.3)
// ============================================================================

/**
 * Post a review-queue record when a template lands in pending-review.
 *
 * Fire-and-forget Slack webhook when the env var is set. Non-blocking.
 */
async function notifyTemplateReviewQueue(params: {
  templateId: string;
  submittedBy: string;
  tenantId: string | null;
  reason: string;
  title: string;
}): Promise<void> {
  try {
    const db = getFirestore(getApp());
    await setDoc(doc(db, `globalTemplateReviewQueue/${params.templateId}`), {
      submittedBy: params.submittedBy,
      submittedAtMs: Date.now(),
      tenantId: params.tenantId,
      templateId: params.templateId,
      reason: params.reason,
      title: params.title,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn('[templateRegistry] review-queue write failed (non-fatal):', err);
  }

  // Optional Slack webhook notification
  const slackWebhook = (
    typeof process !== 'undefined' && process.env && process.env.TEMPLATE_REVIEW_SLACK_WEBHOOK
      ? process.env.TEMPLATE_REVIEW_SLACK_WEBHOOK
      : ''
  ).trim();
  if (slackWebhook) {
    try {
      // Fire and forget
      void fetch(slackWebhook, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: `New legal template pending review: *${params.title}* (id: ${params.templateId})\nSubmitted by: ${params.submittedBy}\nTenant: ${params.tenantId || '(none)'}\nReason: ${params.reason}`,
        }),
      }).catch(() => {
        /* ignore */
      });
    } catch {
      /* ignore */
    }
  }
}

export async function saveReusableTemplate(input: SaveTemplateInput): Promise<SaveTemplateResult> {
  const auth = getAuth();
  const uid = auth.currentUser?.uid;
  if (!uid) {
    throw new Error('You must be signed in to save a reusable template.');
  }

  const db = getFirestore(getApp());
  const templateId = `tpl-${slugifyTitle(input.title)}-${Date.now().toString(36)}`;
  const tenantId = await resolveTenantId();

  const storedBase: StoredTemplateDoc = {
    id: templateId,
    name: input.title,
    nameEs: input.title, // caller populates ES at display time; name mirrors title at save
    description: input.description,
    descriptionEs: input.description,
    category: 'general',
    jurisdiction: 'california',
    fields: input.fields,
    sections: [
      { id: 'freeform', title: 'Document', titleEs: 'Documento', content: '', contentEs: '' },
    ],
    requiresAttorneyReview: input.requiresAttorneyReview,
    isFreeform: true,
    scope: 'user', // overwritten per-destination below
    freeformDrafterPromptEn: input.freeformDrafterPromptEn,
    freeformDrafterPromptEs: input.freeformDrafterPromptEs,
    generatedBody: input.generatedBody,
    authoredLanguage: input.authoredLanguage,
    submittedBy: uid,
    submittedAtMs: Date.now(),
    tenantId: tenantId || undefined,
  };

  if (input.scope === 'user') {
    await setDoc(
      doc(db, `users/${uid}/legalTemplates/${templateId}`),
      { ...storedBase, scope: 'user', createdAt: serverTimestamp() }
    );
    return { templateId, scope: 'user', status: 'active', autoApproved: true };
  }

  if (input.scope === 'tenant') {
    if (!tenantId) {
      throw new Error('Cannot save firm-scoped template: no firm membership detected for this user.');
    }
    await setDoc(
      doc(db, `tenants/${tenantId}/legalTemplates/${templateId}`),
      { ...storedBase, scope: 'tenant', createdAt: serverTimestamp() }
    );
    return { templateId, scope: 'tenant', status: 'active', autoApproved: true };
  }

  // Global-candidate — run auto-approve heuristic
  const autoDecision = isAutoApprovable(input.generatedBody, {
    title: input.title,
    description: input.description,
  });

  const status: 'active' | 'pending-review' = autoDecision.approved ? 'active' : 'pending-review';

  await setDoc(doc(db, `globalLegalTemplates/${templateId}`), {
    ...storedBase,
    scope: 'global',
    status,
    createdAt: serverTimestamp(),
  });

  if (!autoDecision.approved) {
    // Write review queue record (non-blocking best-effort)
    await notifyTemplateReviewQueue({
      templateId,
      submittedBy: uid,
      tenantId: tenantId || null,
      reason: autoDecision.reason || 'Did not pass auto-approve heuristic.',
      title: input.title,
    });
  }

  return {
    templateId,
    scope: 'global',
    status,
    autoApproved: autoDecision.approved,
    reason: autoDecision.approved ? undefined : autoDecision.reason,
  };
}

// ============================================================================
// Lookup helpers (dynamic)
// ============================================================================

/**
 * Resolve a templateId against all scopes — static first, then dynamic. Used
 * by the legalDocCreator tool's generateStep to load a user/tenant/global
 * template at generation time without blocking the picker render path.
 */
export async function getTemplateByIdAnyScope(id: string): Promise<LegalTemplate | undefined> {
  // Static first
  const staticMatch = LEGAL_TEMPLATES.find((t) => t.id === id);
  if (staticMatch) return staticMatch;

  // Dynamic
  try {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    const db = getFirestore(getApp());

    if (uid) {
      const s = await getDoc(doc(db, `users/${uid}/legalTemplates/${id}`));
      if (s.exists()) return storedToTemplate({ ...(s.data() as StoredTemplateDoc), id }, 'user');
    }

    const tenantId = await resolveTenantId();
    if (tenantId) {
      const s = await getDoc(doc(db, `tenants/${tenantId}/legalTemplates/${id}`));
      if (s.exists()) return storedToTemplate({ ...(s.data() as StoredTemplateDoc), id }, 'tenant');
    }

    const g = await getDoc(doc(db, `globalLegalTemplates/${id}`));
    if (g.exists()) {
      const data = g.data() as StoredTemplateDoc;
      if (data.status === 'active') {
        return storedToTemplate({ ...data, id }, 'global');
      }
    }
  } catch (err) {
    console.warn('[templateRegistry] getTemplateByIdAnyScope failed', err);
  }

  return undefined;
}
